import React, { useEffect, useState, useCallback } from "react";
import { ReactComponent as BsInfoCircleFill } from "../../../assets/Icons/Universal/BsInfoCircleFill.svg";
import "../../../styles/ComponentsStyles/Universal/Tables/UniversalMaterialTable.css";

const examData = [
  {
    examName: "First terminal exam",
    examDate: "02 Oct, 2025",
    reportedBy: "Samiul Ittefak",
    items: [
      { name: "Chalk", size: "150 box" },
      { name: "Exam paper", size: "33 pcs" },
      { name: "Exam paper", size: "33 pcs" },
      { name: "Exam paper", size: "33 pcs" },
    ],
  },
  {
    examName: "Second terminal exam",
    examDate: "15 Nov, 2025",
    reportedBy: "John Doe",
    items: [
      { name: "Pen", size: "50 box" },
      { name: "Answer sheet", size: "100 pcs" },
    ],
  },
];

export default function UniversalMaterialTable({
  checkedItems = [],
  setCheckedItems = () => {},
}) {
  // selected is a simple map of keys -> true
  const [selected, setSelected] = useState({});

  // Sync selected map from checkedItems (handles both array and object shapes)
  useEffect(() => {
    const map = {};
    if (Array.isArray(checkedItems)) {
      for (const it of checkedItems) {
        if (it && typeof it === "object" && "examIndex" in it && "itemIndex" in it) {
          map[`${it.examIndex}-${it.itemIndex}`] = true;
        }
      }
    } else if (checkedItems && typeof checkedItems === "object") {
      // previously it may have been a mapping of keys to boolean or to item objects
      for (const [k, v] of Object.entries(checkedItems)) {
        if (v) map[k] = true;
      }
    }
    setSelected(map);
  }, [checkedItems]);

  const makeItemObject = (exam, examIndex, itemIndex, item) => ({
    examIndex,
    itemIndex,
    examName: exam.examName,
    examDate: exam.examDate,
    reportedBy: exam.reportedBy,
    ...item,
  });

  // toggle single item
  const toggleItem = useCallback(
    (exam, examIndex, item, itemIndex) => {
      const key = `${examIndex}-${itemIndex}`;

      // update UI map
      setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

      // update checkedItems (robust to prev shape)
      setCheckedItems((prev) => {
        if (Array.isArray(prev)) {
          // array form: add/remove object
          const arr = prev.slice();
          const idx = arr.findIndex(
            (x) => x?.examIndex === examIndex && x?.itemIndex === itemIndex
          );
          if (idx > -1) arr.splice(idx, 1);
          else arr.push(makeItemObject(exam, examIndex, itemIndex, item));
          return arr;
        } else if (prev && typeof prev === "object") {
          // object form: toggle by key (store item object as value)
          const updated = { ...prev };
          if (updated[key]) delete updated[key];
          else updated[key] = makeItemObject(exam, examIndex, itemIndex, item);
          return updated;
        } else {
          // fallback -> return array
          return [makeItemObject(exam, examIndex, itemIndex, item)];
        }
      });
    },
    [setCheckedItems]
  );

  // toggle whole group (exam)
  const toggleGroup = useCallback(
    (exam, examIndex) => {
      const itemsCount = exam.items.length;
      const allSelected = exam.items.every((_, i) => !!selected[`${examIndex}-${i}`]);

      // update UI map
      setSelected((prev) => {
        const updated = { ...prev };
        for (let i = 0; i < itemsCount; i++) {
          updated[`${examIndex}-${i}`] = !allSelected;
        }
        return updated;
      });

      // update checkedItems (robust)
      setCheckedItems((prev) => {
        if (Array.isArray(prev)) {
          // remove any existing items of this exam
          const others = prev.filter((x) => x?.examIndex !== examIndex);
          if (allSelected) {
            return others; // unselect group
          } else {
            // add all group items
            const newItems = exam.items.map((it, i) => makeItemObject(exam, examIndex, i, it));
            return [...others, ...newItems];
          }
        } else if (prev && typeof prev === "object") {
          const updated = { ...prev };
          for (let i = 0; i < itemsCount; i++) {
            const key = `${examIndex}-${i}`;
            if (allSelected) {
              delete updated[key];
            } else {
              updated[key] = makeItemObject(exam, examIndex, i, exam.items[i]);
            }
          }
          return updated;
        } else {
          // fallback -> array
          if (allSelected) return [];
          return exam.items.map((it, i) => makeItemObject(exam, examIndex, i, it));
        }
      });
    },
    [selected, setCheckedItems]
  );

  // util to check if an item is selected (reads from selected map)
  const isChecked = (examIndex, itemIndex) => !!selected[`${examIndex}-${itemIndex}`];

  // debug
  console.log("checkedItems:", checkedItems);

  return (
    <div className="material-table-container">
      <div className="table-header">
        <h2>
          All material list for examination <BsInfoCircleFill />
        </h2>
      </div>

      <table className="material-table">
        <thead>
          <tr>
            <th>Examination name/date</th>
            <th>Reported by</th>
            <th>Items</th>
            <th>Order size</th>
            <th>Select</th>
          </tr>
        </thead>

        <tbody>
          {examData.map((exam, examIndex) => (
            <React.Fragment key={`exam-${examIndex}`}>
              {exam.items.map((item, itemIndex) => {
                const rowKey = `${examIndex}-${itemIndex}`;
                return (
                  <tr key={rowKey}>
                    {itemIndex === 0 && (
                      <>
                        <td rowSpan={exam.items.length}>
                          <div className="material-info">
                            <strong>{exam.examName}</strong>
                            <div className="material-date">{exam.examDate}</div>
                          </div>
                        </td>
                        <td rowSpan={exam.items.length} className="reported-by">
                          {exam.reportedBy}
                        </td>
                      </>
                    )}

                    <td>{item.name}</td>
                    <td>{item.size}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={isChecked(examIndex, itemIndex)}
                        onChange={() => toggleItem(exam, examIndex, item, itemIndex)}
                      />
                    </td>
                  </tr>
                );
              })}

              <tr className="select-row" key={`select-row-${examIndex}`}>
                <td colSpan={5}>
                  <button
                    className="select-link"
                    onClick={() => toggleGroup(exam, examIndex)}
                  >
                    {exam.items.every((_, i) => isChecked(examIndex, i))
                      ? "Unselect all"
                      : "Select all"}
                  </button>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
